import { UpdateTaskRequest } from "@9amhealth/openapi";
import {
  NineActions,
  NineButton,
  NineHeading
} from "@9amhealth/wcl/generated/react";
import styled from "@emotion/styled";
import type { FC } from "react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import FileType from "src/constants/fileType";
import { PlanPending } from "src/constants/icons";
import translate from "src/lib/translate";
import { KnownGroup, KnownProgram } from "src/state/ProgramBloc/ProgramBloc";
import { taskManagementState, tracker } from "src/state/state";
import { TaskKey } from "src/state/TaskManagementBloc/TaskManagementBloc";
import { AppQueryPopupsController } from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";
import DocumentUploader, {
  DocumentUploaderRef
} from "src/ui/components/DocumentUploader/DocumentUploader";
import InDialog from "src/ui/components/InDialog/InDialog";
import Translate from "src/ui/components/Translate/Translate";
import { useDeferredDocumentUpload } from "src/ui/hooks/useDeferredDocumentUpload";
import useGoToOrBack from "src/ui/hooks/useGoToOrBack";
import { v4 as uuidv4 } from "uuid";

const Container = styled.div`
  max-width: 600px;
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100%;
  padding: var(--space-md);
  text-align: center;

  &[data-loading="true"] {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const StepContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const HelpIcon = styled.div`
  margin-bottom: 1rem;
`;

const SmallText = styled.p`
  font-size: 0.875rem;
  color: var(--color-c-60);
  margin: 0;
  text-align: center;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: var(--color-primary);
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0;

  &:hover {
    color: var(--color-primary-dark);
  }
`;

type Step = "upload" | "alternative" | "help";

const PrescriptionLabelUploadDialog: FC<{ returnUrl: string }> = ({
  returnUrl
}) => {
  const documentSessionId = useMemo<string>(() => uuidv4(), []);
  const [currentStep, setCurrentStep] = useState<Step>("upload");
  const navigate = useGoToOrBack();
  const documentUploaderRef = useRef<DocumentUploaderRef>(null);
  const actionsRef = useRef<HTMLDivElement>(null);

  const {
    documentConfig: baseDocumentConfig,
    handleFileUploadChange,
    handleContinue: handleUpload,
    resetDocuments,
    isUploading,
    hasSelectedFiles
  } = useDeferredDocumentUpload({
    metaFileType: FileType.PRESCRIPTION_LABEL,
    tags: [
      "prescription-label",
      `document-upload-session:${documentSessionId}`
    ],
    onUploadComplete: onClose
  });

  const documentConfig = useMemo(() => {
    const config = { ...baseDocumentConfig };
    Object.keys(config).forEach((docId) => {
      const docConfig = config[docId];
      if (docConfig && docConfig.tags) {
        config[docId] = {
          ...docConfig,
          tags: [...docConfig.tags, `page-create-time-${docId}`]
        };
      }
    });
    return config;
  }, [baseDocumentConfig]);

  function onClose() {
    AppQueryPopupsController.closePopup();
    navigate(returnUrl, {
      multiBack: true,
      replace: true
    });
  }

  const handleSkip = () => {
    tracker.track("Prescription Label Upload Skipped");
    void taskManagementState
      .updateTaskStatus(
        {
          program: KnownProgram.ONBOARDING,
          group: KnownGroup.GLP1_CONTINUATION,
          slug: TaskKey.UPLOAD_PRESCRIPTION_LABEL
        },
        UpdateTaskRequest.status.SKIPPED
      )
      .then(() => {
        AppQueryPopupsController.closePopup();
      });
  };

  const handleContinue = () => {
    void handleUpload(documentUploaderRef, onClose);
  };

  const handleMissingDocuments = () => {
    setCurrentStep("help");
  };

  const handleBackToUpload = () => {
    setCurrentStep("upload");
  };

  const handleAskForAlternative = () => {
    setCurrentStep("alternative");
  };

  const handleFileSelected = () => {
    // Scroll to the action button when a file is selected
    setTimeout(() => {
      actionsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest"
      });
    }, 300);
  };

  useEffect(() => {
    // Reset documents when step changes
    resetDocuments();
  }, [currentStep, resetDocuments]);

  const renderStepContent = () => {
    switch (currentStep) {
      case "upload":
        return (
          <StepContent>
            <NineHeading>
              <h4 className="as-h4-large">
                <Translate msg="prescriptionLabelUpload.step1.title" />
              </h4>
            </NineHeading>
            <p>
              <Translate msg="prescriptionLabelUpload.step1.description" />
            </p>
            <DocumentUploader
              display={{
                ratio: 0.8,
                showSelectedTitles: false,
                action: "delete"
              }}
              config={{
                fileTypes: [
                  "image/jpeg",
                  "image/png",
                  "application/pdf",
                  "image/jpg"
                ],
                files: documentConfig
              }}
              onChange={handleFileUploadChange}
              onFileSelected={handleFileSelected}
              deferUpload={true}
              ref={documentUploaderRef}
            />
            <div ref={actionsRef}>
              <NineActions style={{ justifyContent: "center" }}>
                <NineButton
                  disabled={hasSelectedFiles ? "false" : "true"}
                  onClick={handleContinue}
                >
                  <Translate msg="continue" />
                </NineButton>
              </NineActions>
            </div>
            <SmallText>
              <Translate msg="prescriptionLabelUpload.missingLabel" />
              <br />
              <LinkButton onClick={handleAskForAlternative}>
                <Translate msg="prescriptionLabelUpload.letUsKnow" />
              </LinkButton>
            </SmallText>
          </StepContent>
        );

      case "alternative":
        return (
          <StepContent>
            <NineHeading>
              <h4 className="as-h4-large">
                <Translate msg="prescriptionLabelUpload.step2.title" />
              </h4>
            </NineHeading>
            <p>
              <Translate msg="prescriptionLabelUpload.step2.description" />
            </p>
            <DocumentUploader
              display={{
                ratio: 0.8,
                showSelectedTitles: false,
                action: "delete"
              }}
              config={{
                fileTypes: [
                  "image/jpeg",
                  "image/png",
                  "application/pdf",
                  "image/jpg"
                ],
                files: documentConfig
              }}
              onChange={handleFileUploadChange}
              onFileSelected={handleFileSelected}
              deferUpload={true}
              ref={documentUploaderRef}
            />
            <div ref={actionsRef}>
              <NineActions style={{ justifyContent: "center" }}>
                <NineButton
                  disabled={hasSelectedFiles ? "false" : "true"}
                  onClick={handleContinue}
                >
                  <Translate msg="continue" />
                </NineButton>
              </NineActions>
            </div>
            <SmallText>
              <Translate msg="prescriptionLabelUpload.missingAlternative" />
              <br />
              <LinkButton onClick={handleMissingDocuments}>
                <Translate msg="prescriptionLabelUpload.letUsKnow" />
              </LinkButton>
            </SmallText>
          </StepContent>
        );

      case "help":
        return (
          <StepContent>
            <HelpIcon>
              <PlanPending size={64} />
            </HelpIcon>
            <NineHeading>
              <h4 className="as-h4-large">
                <Translate msg="prescriptionLabelUpload.help.title" />
              </h4>
            </NineHeading>
            <p>
              <Translate msg="prescriptionLabelUpload.help.description" />
            </p>
            <NineActions
              style={{ justifyContent: "center", marginBottom: "1rem" }}
            >
              <NineButton onClick={handleSkip}>
                <Translate msg="confirm" />
              </NineButton>
            </NineActions>

            <SmallText>
              <Translate msg="prescriptionLabelUpload.help.foundItQuestion" />
              <br />
              <LinkButton onClick={handleBackToUpload}>
                <Translate msg="prescriptionLabelUpload.help.backToUpload" />
              </LinkButton>
            </SmallText>
          </StepContent>
        );

      default:
        return null;
    }
  };

  return (
    <InDialog
      popup
      title={translate("prescriptionLabelUpload.title")}
      returnUrl={returnUrl}
      onClose={onClose}
    >
      <Container data-loading={isUploading}>{renderStepContent()}</Container>
    </InDialog>
  );
};

export default PrescriptionLabelUploadDialog;
